/**
 * Design tokens — single source of truth for FSE visual identity.
 *
 * Platform-agnostic: pure data, no MUI/RN imports. Web (MUI) and mobile
 * (react-native-paper) consume these tokens via their respective adapters.
 *
 * Three-layer model (mirrors `Documentation/Claude design files/colors_and_type.css`):
 *
 *   1. BRAND SCALE — raw FSE colors: mango / ripe / leaf / earth / paper / alert.
 *      Components never reference these directly.
 *   2. MD3 SYSTEM ROLES — `mdSysColor.*` per material-components-android/Color.md.
 *      Maps brand scale onto the MD3 role contract.
 *   3. SEMANTIC ALIASES — short app-level names (`tokens.primary`, `tokens.highlight`)
 *      and the legacy `paletteLight`/`paletteDark` shape that web and mobile
 *      adapters consume.
 *
 * Swap implication: if MUI or Paper is replaced, tokens stay; only the adapter
 * (theme.ts / paperTheme.ts) changes.
 */

// ============================================================================
// 1. BRAND SCALE — verbatim from FSE design fileset
// ============================================================================

const mango = {
  50: '#FEF7E3',
  100: '#FCEDB7',
  200: '#FADF82',
  300: '#F8CE54',
  400: '#F6C148',
  500: '#F5B53C', // brand secondary / tertiary accent
  600: '#DC9C20',
  700: '#B47C15',
  800: '#8B5E10',
} as const;

const ripe = {
  50: '#FDEEDF',
  100: '#FACFAB',
  200: '#F6AC74',
  300: '#EE8E45',
  500: '#E87B2C',
  600: '#E07A1F', // money-positive / achievement highlight
  700: '#B85F14',
} as const;

const leaf = {
  50: '#E9F2EB',
  100: '#C5DDCB',
  200: '#97C0A2',
  400: '#4F8E63',
  600: '#347746', // secondary
  700: '#2D6B3F', // brand primary
  900: '#163D22',
} as const;

const earth = {
  900: '#1F1A14',
  700: '#4A4034',
  500: '#7A6E5E',
  300: '#B4A98F',
} as const;

const paper = {
  50: '#FFFFFF',
  100: '#F6F6F4',
  200: '#EBEBE8',
  300: '#D9D9D5',
} as const;

const alert = {
  50: '#FBE6E3',
  500: '#D34A40',
  600: '#C0342B',
  900: '#6E1A14',
} as const;

export const brand = { mango, ripe, leaf, earth, paper, alert } as const;

// ============================================================================
// 2. MD3 SYSTEM ROLES — light + dark
// Maps brand scale → MD3 role contract (Color.md).
// ============================================================================

const mdSysColorLight = {
  // Primary (leaf green — CTAs, buttons, FAB)
  primary: leaf[700],
  onPrimary: '#FFFFFF',
  primaryContainer: leaf[100],
  onPrimaryContainer: leaf[900],

  // Secondary (leaf-600 — selection, success affordances)
  secondary: leaf[600],
  onSecondary: '#FFFFFF',
  secondaryContainer: leaf[100],
  onSecondaryContainer: leaf[900],

  // Tertiary (mango yellow — highlights, FSE brand accent)
  tertiary: mango[500],
  onTertiary: earth[900],
  tertiaryContainer: mango[100],
  onTertiaryContainer: mango[800],

  // Error
  error: alert[600],
  onError: paper[50],
  errorContainer: alert[50],
  onErrorContainer: alert[900],

  // Surfaces
  background: paper[50],
  onBackground: earth[900],
  surface: paper[50],
  onSurface: earth[900],
  surfaceVariant: paper[100],
  onSurfaceVariant: earth[700],

  // Surface container tiers (MD3 replaces elevation overlays with tonal tiers)
  surfaceContainerLowest: '#FFFFFF',
  surfaceContainerLow: '#FAFAF9',
  surfaceContainer: '#F4F4F2',
  surfaceContainerHigh: '#EBEBE8',
  surfaceContainerHighest: '#E0E0DC',

  // Outlines
  outline: earth[500],
  outlineVariant: paper[200],

  // Inverse
  inverseSurface: earth[900],
  inverseOnSurface: '#FFFFFF',
  inversePrimary: leaf[200],

  // Scrim
  scrim: 'rgba(31, 26, 20, 0.55)',
} as const;

const mdSysColorDark = {
  // Lighten primary for contrast on dark surfaces; deepen containers
  primary: leaf[200],
  onPrimary: leaf[900],
  primaryContainer: leaf[700],
  onPrimaryContainer: leaf[100],

  secondary: leaf[200],
  onSecondary: leaf[900],
  secondaryContainer: leaf[700],
  onSecondaryContainer: leaf[100],

  tertiary: mango[300],
  onTertiary: earth[900],
  tertiaryContainer: mango[700],
  onTertiaryContainer: mango[100],

  error: alert[500],
  onError: '#FFFFFF',
  errorContainer: '#5A1818',
  onErrorContainer: '#FECACA',

  background: '#161613',
  onBackground: paper[50],
  surface: '#1F1E1B',
  onSurface: paper[50],
  surfaceVariant: '#2A2925',
  onSurfaceVariant: paper[200],

  surfaceContainerLowest: '#161613',
  surfaceContainerLow: '#1F1E1B',
  surfaceContainer: '#252420',
  surfaceContainerHigh: '#2F2E29',
  surfaceContainerHighest: '#3A3833',

  outline: earth[300],
  outlineVariant: '#3A3833',

  inverseSurface: paper[50],
  inverseOnSurface: earth[900],
  inversePrimary: leaf[700],

  scrim: 'rgba(0, 0, 0, 0.55)',
} as const;

export const mdSysColor = { light: mdSysColorLight, dark: mdSysColorDark } as const;

// ============================================================================
// 3. SEMANTIC ALIASES + LEGACY palette shape
// ============================================================================

// Build ramps in the existing `paletteLight.primary[700]` shape so that
// `theme.ts` (web) and `paperTheme.ts` (mobile) keep compiling without a
// rewrite — but each shade now points at an FSE brand value.

// Primary ramp = leaf, filled in across MUI's expected shades (300/400/700/800/900).
const greenRamp = {
  50: leaf[50],
  100: leaf[100],
  200: leaf[200],
  300: leaf[200], // MUI 300 — use leaf-200 (no leaf-300 in brand scale)
  400: leaf[400],
  500: leaf[400], // MUI 500 — use leaf-400
  600: leaf[600],
  700: leaf[700], // brand primary
  800: leaf[900], // deepen for "dark" shade
  900: leaf[900],
} as const;

// Secondary ramp = mango, mapped onto MUI shade keys
const mangoRamp = {
  50: mango[50],
  100: mango[100],
  200: mango[200],
  300: mango[300],
  400: mango[400],
  500: mango[500], // brand secondary
  600: mango[600],
  700: mango[700],
  800: mango[800],
  900: mango[800],
} as const;

const semantic = {
  success: leaf[600],
  warning: mango[600],
  danger: alert[600],
  info: '#0ea5e9',
  highlight: ripe[600], // money-positive / achievement
} as const;

// ----- Light mode -----------------------------------------------------------
export const paletteLight = {
  primary: { ...greenRamp, contrast: '#ffffff' },
  secondary: { ...mangoRamp, contrast: earth[900] },
  neutral: {
    bg: paper[50],
    surface: paper[100],
    surfaceVariant: paper[200],
    border: paper[300],
    text: earth[900],
    textMuted: earth[500],
    textDisabled: earth[300],
  },
  semantic,
} as const;

// ----- Dark mode ------------------------------------------------------------
export const paletteDark = {
  primary: {
    ...greenRamp,
    // Lighten "main" for dark surfaces — `theme.ts` reads `primary[400]` in dark.
    contrast: leaf[900],
  },
  secondary: { ...mangoRamp, contrast: earth[900] },
  neutral: {
    bg: '#161613',
    surface: '#1F1E1B',
    surfaceVariant: '#2A2925',
    border: '#3A3833',
    text: paper[50],
    textMuted: earth[300],
    textDisabled: '#525049',
  },
  semantic,
} as const;

// Backwards-compatible default export — referenced by older imports.
export const palette = paletteLight;

// ============================================================================
// TYPE — families + scale (rural-bumped per FSE design — 16px body floor)
// ============================================================================

export const typography = {
  // Display: Bricolage Grotesque (headlines, big numbers)
  // Body: Noto Sans (everything else, multi-script coverage)
  // Mono: system mono for tabular numerics where Noto Sans variant isn't enough
  // Web CSS font stacks. "Bricolage Grotesque Variable" is the family name
  // registered by @fontsource-variable/bricolage-grotesque (self-hosted, no
  // Google Fonts CDN). "Noto Sans" is self-hosted via @fontsource/noto-sans.
  fontFamilyDisplay:
    '"Bricolage Grotesque Variable", "Bricolage Grotesque", "Montserrat", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
  fontFamily:
    '"Noto Sans", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
  fontFamilyMono:
    '"Roboto Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',

  // React Native font names. RN matches a single registered font, not a CSS
  // stack — these are the names the bundled .ttf is loaded under (see
  // apps/mobile/app/_layout.tsx useFonts + paperTheme configureFonts).
  // Body deliberately stays on the platform system font (Noto Sans on Android,
  // SF on iOS) per the design's stated fallback — only display is bundled.
  fontFamilyDisplayNative: 'BricolageGrotesque',

  // MD3 type scale, with smallest sizes bumped for rural accessibility
  // (12sp → 13px, 14sp → 16px, 16sp → 18px default body)
  displayLarge: { fontSize: 57, lineHeight: 1.15, fontWeight: 800 },
  displayMedium: { fontSize: 45, lineHeight: 1.15, fontWeight: 800 },
  displaySmall: { fontSize: 36, lineHeight: 1.15, fontWeight: 700 },
  headlineLarge: { fontSize: 32, lineHeight: 1.2, fontWeight: 700 },
  headlineMedium: { fontSize: 28, lineHeight: 1.2, fontWeight: 700 },
  headlineSmall: { fontSize: 24, lineHeight: 1.25, fontWeight: 700 },
  titleLarge: { fontSize: 22, lineHeight: 1.3, fontWeight: 600 },
  titleMedium: { fontSize: 18, lineHeight: 1.35, fontWeight: 600 },
  titleSmall: { fontSize: 15, lineHeight: 1.4, fontWeight: 600 },
  bodyLarge: { fontSize: 18, lineHeight: 1.5, fontWeight: 400 }, // default body
  bodyMedium: { fontSize: 16, lineHeight: 1.5, fontWeight: 400 },
  bodySmall: { fontSize: 14, lineHeight: 1.5, fontWeight: 400 },
  labelLarge: { fontSize: 14, lineHeight: 1.4, fontWeight: 600 },
  labelMedium: { fontSize: 13, lineHeight: 1.4, fontWeight: 600 },
  labelSmall: { fontSize: 12, lineHeight: 1.4, fontWeight: 600 },

  // Legacy aliases kept for existing consumers (h1..h4, body, caption, button)
  h1: { fontSize: 32, lineHeight: 1.25, fontWeight: 700 },
  h2: { fontSize: 24, lineHeight: 1.3, fontWeight: 700 },
  h3: { fontSize: 20, lineHeight: 1.4, fontWeight: 600 },
  h4: { fontSize: 18, lineHeight: 1.4, fontWeight: 600 },
  body: { fontSize: 16, lineHeight: 1.5, fontWeight: 400 },
  caption: { fontSize: 13, lineHeight: 1.4, fontWeight: 400 },
  button: { fontSize: 14, lineHeight: 1.5, fontWeight: 600, textTransform: 'none' as const },
} as const;

// ============================================================================
// SPACING — 4dp base grid (MD3 standard)
// ============================================================================

export const spacing = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,
  // FSE-named aliases (1-indexed, matching --space-1..10 in design CSS)
  s1: 4,
  s2: 8,
  s3: 12,
  s4: 16,
  s5: 20,
  s6: 24,
  s7: 32,
  s8: 40,
  s9: 48,
  s10: 64,
} as const;

// ============================================================================
// SHAPE — MD3 corner scale
// ============================================================================

export const radius = {
  none: 0,
  sm: 4, // text fields
  md: 8, // chips
  lg: 12, // cards
  xl: 16, // large cards, FAB
  xxl: 28, // sheets, large FAB
  full: 9999, // buttons, active indicators
  // FSE-named aliases (MD3 spec names)
  cornerExtraSmall: 4,
  cornerSmall: 8,
  cornerMedium: 12,
  cornerLarge: 16,
  cornerLargeIncreased: 20,
  cornerExtraLarge: 28,
  cornerExtraExtraLarge: 48,
  cornerFull: 9999,
} as const;

// ============================================================================
// ELEVATION — MD3 0-5dp; tonal hierarchy does most of the work
// ============================================================================

export const elevation = {
  none: '0 0 0 rgba(0,0,0,0)',
  low: '0 1px 2px rgba(31,26,20,0.30), 0 1px 3px 1px rgba(31,26,20,0.15)',
  medium: '0 1px 2px rgba(31,26,20,0.30), 0 2px 6px 2px rgba(31,26,20,0.15)',
  high: '0 1px 3px rgba(31,26,20,0.30), 0 4px 8px 3px rgba(31,26,20,0.15)',
  level0: '0 0 0 rgba(0,0,0,0)',
  level1: '0 1px 2px rgba(31,26,20,0.30), 0 1px 3px 1px rgba(31,26,20,0.15)',
  level2: '0 1px 2px rgba(31,26,20,0.30), 0 2px 6px 2px rgba(31,26,20,0.15)',
  level3: '0 1px 3px rgba(31,26,20,0.30), 0 4px 8px 3px rgba(31,26,20,0.15)',
  level4: '0 2px 3px rgba(31,26,20,0.30), 0 6px 10px 4px rgba(31,26,20,0.15)',
  level5: '0 4px 4px rgba(31,26,20,0.30), 0 8px 12px 6px rgba(31,26,20,0.15)',
} as const;

// ============================================================================
// TOUCH TARGETS — rural-bumped above MD3's 48dp
// ============================================================================

export const tap = {
  sm: 44, // secondary actions
  md: 56, // default; rows, fields
  lg: 64, // primary CTA, M3 Expressive Large
} as const;

// ============================================================================
// STATE LAYER — MD3 press/hover/focus overlays
// ============================================================================

export const stateLayer = {
  press: 'rgba(31,26,20,0.12)',
  hover: 'rgba(31,26,20,0.08)',
  focus: leaf[600], // secondary color as focus ring
} as const;

// ============================================================================
// MOTION — MD3 easings + durations
// ============================================================================

export const motion = {
  fast: 100,
  base: 200,
  slow: 320,
  ease: 'cubic-bezier(0.2, 0, 0, 1)',
  easeEmphasized: 'cubic-bezier(0.2, 0, 0, 1)',
  easeDecelerate: 'cubic-bezier(0, 0, 0, 1)',
  easeAccelerate: 'cubic-bezier(0.3, 0, 1, 1)',
} as const;

// ============================================================================
// EXPORTS
// ============================================================================

export type ColorMode = 'light' | 'dark';

export const tokens = {
  brand,
  mdSysColor,
  paletteLight,
  paletteDark,
  palette: paletteLight, // legacy alias
  typography,
  spacing,
  radius,
  elevation,
  tap,
  stateLayer,
  motion,
} as const;

export type FseTokens = typeof tokens;
